import React, { useContext } from 'react'
import cn from 'classnames'
import { DoxRequest } from '@grammarly/http-clients-dox'

import { TypeSectionGrammarCheck } from '../../../lib/types'
import { useApiService, useBrowserService, useExperimentService } from '../../../lib/hooks'
import { PageSection } from '../../page-section'
import { ContentProps } from '../../renderer'
import { SectionContext } from '../../../contexts/section-context'
import { PageContext } from '../../../contexts/page-context'
import { CompactSectionsContext } from '../../../contexts/compact-sections-context'
import { ToolLayoutContext } from '../../../contexts/tool-layout-context'
import { Tool } from '@components/tool'

import { TextCheckerReport } from './report-flow/form'
import { TextCheckerEditorPlugin } from './editor-plugin'

import styles from './text-checker.module.scss'
import { useIntl } from 'react-intl'

const title = 'Here’s your personalized workspace.'

const subheader = 'Explore Grammarly’s suggestions to ensure your writing is at its best.\n\n'

const isGrammarCheckMobileEditorPlugin = (
  pageSlug: string,
  isMobile: boolean,
  scanForPlagiarism?: boolean,
  isWordCounter?: boolean
) => {
  return isMobile && (pageSlug === 'mobile-grammar-check' || !scanForPlagiarism || isWordCounter)
}

const TextCheckerSectionContent: React.FC<ContentProps<TypeSectionGrammarCheck>> = (props) => {
  const {
    isEditorPluginEnabled,
    ctaText,
    placeholderText,
    isFileUploadEnabled,
    scanForPlagiarism,
    toolVariant,
    bottomSectionLabel,
  } = props.structure.fields

  const intl = useIntl()
  const { slug } = useContext(PageContext)
  const { isHero } = useContext(SectionContext)
  const { isMobileOrTablet } = useBrowserService()
  const { dox } = useApiService()
  const { isTools2_XWithLoggedInEnabled } = useExperimentService()
  const isWordCounter = toolVariant === 'WORD_COUNTER'
  const isToolsRedesignEnabled = isTools2_XWithLoggedInEnabled()

  const createDocument = async (text: string) => {
    const docText = text.trim()

    const doc = {
      doc: {
        ops: [
          { insert: title },
          { insert: '\n', attributes: { header: 1 } as const },
          { insert: subheader },
          { insert: '\n' },
          { insert: '\n' },
          { insert: docText },
        ],
      },
      first_content: (title + '\n' + subheader + '\n\n' + docText).substring(0, 250),
    }
    const document = DoxRequest.Create.textFull(doc)
    const createdDoc = await dox.createDocument(document)
    return createdDoc.id
  }

  const showEditorPlugin = isMobileOrTablet
    ? isGrammarCheckMobileEditorPlugin(slug, isMobileOrTablet, scanForPlagiarism, isWordCounter)
    : isEditorPluginEnabled

  const minimumWordCount = slug === 'grammar-check' ? 1 : 40 // TODO expose `minimum word count` as a field for this content model in Contentful

  // Only support plagiarism checker in Tools Redesign 2.X for now.
  if (isToolsRedesignEnabled && scanForPlagiarism) {
    return (
      <Tool
        toolType="plagiarismChecker"
        outputTitle="Plagiarism Checker results"
        maxWordLimit={1500}
        minWordLimit={40}
        submitButtonText={intl.formatMessage({
          id: 'textChecker.sectionContent.ctaText',
          description: 'Text Checker - Section Content - CTA Text',
          defaultMessage: 'Scan for plagiarism',
        })}
        anonymousUsageLimit={1}
      />
    )
  }

  return (
    <>
      {showEditorPlugin ? (
        <>
          <TextCheckerEditorPlugin
            autofocus={isHero}
            createDocument={createDocument}
            isWordCounter={isWordCounter}
            bottomSectionLabel={bottomSectionLabel}
          />
        </>
      ) : (
        <TextCheckerReport
          autofocus={isHero}
          minimumWordCount={minimumWordCount}
          buttonCtaText={ctaText}
          placeholderText={placeholderText}
          isFileUploadEnabled={isFileUploadEnabled}
          scanForPlagiarism={scanForPlagiarism}
        />
      )}
    </>
  )
}

export const TextCheckerSection: React.FC<ContentProps<TypeSectionGrammarCheck>> = ({ className, structure }) => {
  const {
    sys,
    fields: { internalName, background, heading, scanForPlagiarism },
  } = structure
  const { isHero } = useContext(SectionContext)

  // For some reason, plagiarism checker in the hero is already compact (unlike the other tools).
  // May also want to clean up ToolLayoutContext later as well.
  const { isTools2_XWithLoggedInEnabled } = useExperimentService()
  const isToolsRedesignEnabled = isTools2_XWithLoggedInEnabled()
  const isCompact = (!!isHero && !!scanForPlagiarism) || isToolsRedesignEnabled

  return (
    <ToolLayoutContext.Provider value={true}>
      <CompactSectionsContext.Provider value={isCompact}>
        <PageSection
          internalName={internalName}
          sysStructure={sys}
          className={cn(styles.container, className)}
          heading={heading}
          background={background}
          isToolsRedesign2_XEnabled={isToolsRedesignEnabled}
        >
          <TextCheckerSectionContent structure={structure} />
        </PageSection>
      </CompactSectionsContext.Provider>
    </ToolLayoutContext.Provider>
  )
}
